import { useEffect, useState } from "react";
import { Phone, Sparkles, Mic, CreditCard } from "lucide-react";
import api from "../utils/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * One-shot intro modal explaining the Voice Receptionist 45-minute cap to
 * legacy testers / cert beta testers / regular beta testers.
 *
 * Why: these users have unlimited FREE access to the rest of the app, but
 * Voice has a real per-minute cost (ElevenLabs + Twilio) so we cap it the
 * same way paying users are. This modal sets that expectation explicitly
 * before they hit the cap, so the "wait, I'm a legacy user, why am I being
 * asked to buy a top-up?" support question never lands.
 *
 * Behaviour:
 *   • Polls GET /api/voice-receptionist/intro-nag on app load
 *   • Renders only if backend says should_show=true
 *   • Closing the modal POSTs /intro-nag/dismiss — never shown again to
 *     that user (idempotent on backend so a double-tap is safe)
 *   • Admins are excluded server-side
 *
 * Mounted once in the authenticated layout shell so it fires for any
 * legacy/beta tester on their next app load after the feature ships.
 */
export default function VoiceAllowanceIntroModal() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/voice-receptionist/intro-nag");
        if (!mounted) return;
        if (r.data?.should_show) {
          setPayload(r.data);
          setOpen(true);
        }
      } catch {
        // Silent fail — never block the app for a missing nag endpoint
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/voice-receptionist/intro-nag/dismiss");
    } catch {
      // Even if the dismiss POST fails, close the modal so the user isn't
      // trapped. They'll see it once more on next app load if the network
      // recovers — that's acceptable. Better than blocking.
    } finally {
      setOpen(false);
      setDismissing(false);
    }
  };

  if (!payload) return null;
  const minutes = payload.included_minutes || 45;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="voice-intro-nag-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-amber-500" />
            {payload.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Hero */}
          <div className="flex items-start gap-3 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
            <div className="bg-amber-100 rounded-lg p-2 flex-shrink-0">
              <Phone className="h-5 w-5 text-amber-700" />
            </div>
            <div className="text-sm text-amber-900 leading-relaxed whitespace-pre-line">
              {payload.message}
            </div>
          </div>

          {/* Key facts row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
              <Mic className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
              <div className="text-2xl font-bold text-emerald-700">{minutes}</div>
              <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wider">
                Free minutes / month
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
              <CreditCard className="h-5 w-5 text-blue-600 mx-auto mb-1" />
              <div className="text-2xl font-bold text-blue-700">Top-up</div>
              <div className="text-[11px] font-semibold text-blue-800 uppercase tracking-wider">
                If you need more
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            onClick={handleDismiss}
            disabled={dismissing}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
            data-testid="voice-intro-nag-acknowledge-btn"
          >
            Got it — thanks!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
